// src/components/pages/positions/SodPositions.js
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deletePosition, fetchSodPositions } from "../../../services/Axios";
import {
  getSodPositionsColumns,
  MOBILE_HEADERS,
} from "../../table/columnsConfig/sodPositionsColumns";

export default function SodPositions() {
  const theme = useTheme();
  const navigate = useNavigate();

  // Get column configuration
  const columns = getSodPositionsColumns();
  // Filter out Actions column for data columns
  const dataColumns = columns.filter((col) => col.field !== "Actions");
  const actionsColumn = columns.find((col) => col.field === "Actions");

  const [sodPositions, setSodPositions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [globalFilter, setGlobalFilter] = useState(() => {
    return localStorage.getItem("sodPositionsGlobalFilter") || "";
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Track screen size for responsive table
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await fetchSodPositions();
        if (isMounted) {
          // Handle the nested data structure from the API
          const positions = data || [];
          setSodPositions(positions);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching SOD positions:", error);
          setError("Failed to fetch SOD positions. Please try again.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deletePosition(selectedRow.id);
      const updatedData = await fetchSodPositions();
      setSodPositions(updatedData || []);
    } catch (error) {
      console.error("Error deleting position:", error);
      setError("Failed to delete position. Please try again.");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedRow(null);
    }
  };

  const sortedPositions = useMemo(() => {
    if (!sortConfig.key) return sodPositions;
    return [...sodPositions].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortConfig.direction === "asc" ? -1 : 1;
      if (bVal == null) return sortConfig.direction === "asc" ? 1 : -1;

      // Handle numeric values
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      // Handle string values
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [sodPositions, sortConfig]);

  const filteredData = useMemo(() => {
    if (!globalFilter) return sortedPositions;
    return sortedPositions.filter((item) =>
      Object.values(item).some(
        (val) =>
          val != null &&
          val.toString().toLowerCase().includes(globalFilter.toLowerCase()),
      ),
    );
  }, [sortedPositions, globalFilter]);

  // Save global filter to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "sodPositionsGlobalFilter",
      globalFilter === undefined ? "" : globalFilter,
    );
  }, [globalFilter]);

  const formatFieldValue = (value, field) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    
    // Special formatting for Start Quantity field
    if (field === "Start") {
      const numValue = Number(value);
      if (isNaN(numValue)) return String(value);
      
      if (numValue < 0) {
        // Format negative numbers with parentheses (accounting style)
        return `(${Math.abs(numValue).toLocaleString()})`;
      } else {
        return numValue.toLocaleString();
      }
    }
    
    return String(value);
  };

  const getColumnConfig = (field) => {
    return columns.find((col) => col.field === field);
  };

  return (
    <Box sx={{ p: { xs: 0.5, sm: 2 } }}>
      <Typography
        variant="h2"
        // fontSize="1.5rem"
        fontWeight="bold"
        mb={1}
        mt={2}
      >
        Start of Day Positions
      </Typography>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: theme.palette.background.paper,
          p: { xs: 1, sm: 2 },
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          mx: { xs: 1, sm: 0 },
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate("/Create_Position")}
          aria-label="Create new position"
          sx={{
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          Create
        </Button>
        <TextField
          size="small"
          placeholder="Search positions..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          sx={{
            minWidth: { xs: 150, sm: 200 },
            maxWidth: { xs: "100%", sm: 300 },
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.default,
            },
          }}
          aria-label="Search positions"
        />
      </Box>

      {/* Table section */}
      <Box sx={{ mx: { xs: 1, sm: 0 } }}>
          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              overflowX: "auto",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              maxWidth: "100vw",
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress aria-label="Loading positions" />
              </Box>
            ) : (
              <Table
                size="small"
                stickyHeader
                aria-label="Start of Day Positions Table"
                sx={{
                  fontSize: "0.75rem",
                  "& .MuiTableCell-root": {
                    fontSize: "0.75rem",
                    padding: { xs: "2px 4px", sm: "4px 6px" },
                    borderRight: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    backgroundColor: theme.palette.grey[100],
                    fontWeight: 600,
                    fontSize: { xs: "0.78rem", sm: "0.84rem" }, // Increased by 20%
                    padding: { xs: "4px 6px", sm: "6px 8px" },
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: 1.3,
                  },
                  "& .MuiTableRow-root:hover": {
                    backgroundColor: theme.palette.grey[400],
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {/* Actions column first */}
                    {actionsColumn && (
                      <TableCell
                        align={actionsColumn.align}
                        sx={{
                          py: 0.5,
                          px: 1,
                          width: `${actionsColumn.width}px`,
                          minWidth: `${actionsColumn.width}px`,
                          maxWidth: `${actionsColumn.width}px`,
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          lineHeight: 1.3,
                        }}
                        aria-label={actionsColumn.label}
                      >
                        {actionsColumn.label}
                      </TableCell>
                    )}
                    {/* Show all headers on desktop, only mobile headers on mobile */}
                    {(isMobile
                      ? dataColumns.filter((col) =>
                          MOBILE_HEADERS.includes(col.field),
                        )
                      : dataColumns
                    ).map((column) => (
                      <TableCell
                        key={column.field}
                        align={column.align}
                        sx={{
                          py: 0.5,
                          px: 1,
                          width: `${column.width}px`,
                          minWidth: `${column.width}px`,
                          maxWidth: `${column.width}px`,
                          display: {
                            xs: MOBILE_HEADERS.includes(column.field)
                              ? "table-cell"
                              : "none",
                            lg: "table-cell",
                          },
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          lineHeight: 1.3,
                        }}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={sortConfig.key === column.field}
                            direction={
                              sortConfig.key === column.field
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSort(column.field)}
                            aria-label={`Sort by ${column.label}`}
                            sx={{
                              fontSize: "0.7rem",
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                              lineHeight: 1.3,
                            }}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        align="center"
                      >
                        {loading ? "Loading..." : "No positions found."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData?.map((row) => (
                      <TableRow key={row.id} hover>
                        {/* Actions column first */}
                        {actionsColumn && (
                          <TableCell
                            align={actionsColumn.align}
                            sx={{
                              py: { xs: 0.25, sm: 0.5 },
                              px: { xs: 0.5, sm: 1 },
                              fontSize: { xs: "0.7rem", sm: "0.75rem" },
                              width: `${actionsColumn.width}px`,
                              minWidth: `${actionsColumn.width}px`,
                              maxWidth: `${actionsColumn.width}px`,
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="center"
                              gap={{ xs: 0.25, sm: 0.5 }}
                            >
                              <IconButton
                                onClick={() =>
                                  navigate(`/Edit_Position/${row.id}`)
                                }
                                size="small"
                                aria-label={`Edit position ${row.id}`}
                                sx={{
                                  padding: { xs: "1px", sm: "2px" },
                                  minWidth: { xs: "24px", sm: "auto" },
                                  height: { xs: "24px", sm: "auto" },
                                }}
                              >
                                <EditIcon
                                  sx={{
                                    fontSize: { xs: "0.8rem", sm: "1rem" },
                                  }}
                                />
                              </IconButton>
                              <IconButton
                                onClick={() =>
                                  navigate(`/Copy_Position/${row.id}`)
                                }
                                size="small"
                                color="primary"
                                aria-label={`Copy position ${row.id}`}
                                sx={{
                                  padding: { xs: "1px", sm: "2px" },
                                  minWidth: { xs: "24px", sm: "auto" },
                                  height: { xs: "24px", sm: "auto" },
                                }}
                              >
                                <CopyIcon
                                  sx={{
                                    fontSize: { xs: "0.8rem", sm: "1rem" },
                                  }}
                                />
                              </IconButton>
                              <IconButton
                                onClick={() => handleDeleteClick(row)}
                                size="small"
                                sx={{
                                  color: theme.palette.error.main,
                                  padding: { xs: "1px", sm: "2px" },
                                  minWidth: { xs: "24px", sm: "auto" },
                                  height: { xs: "24px", sm: "auto" },
                                }}
                                aria-label={`Delete position ${row.id}`}
                              >
                                <DeleteIcon
                                  sx={{
                                    fontSize: { xs: "0.8rem", sm: "1rem" },
                                  }}
                                />
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                        {dataColumns.map((column) => {
                          const cellValue = formatFieldValue(
                            row[column.field],
                            column.field,
                          );
                          const isStartField = column.field === "Start";
                          // Check if the value is negative for Start field
                          const isNegative =
                            isStartField &&
                            row[column.field] != null &&
                            Number(row[column.field]) < 0;
                          
                          return (
                            <TableCell
                              key={column.field}
                              align={column.align}
                              sx={{
                                py: { xs: 0.25, sm: 0.5 },
                                px: { xs: 0.5, sm: 1 },
                                display: {
                                  xs: MOBILE_HEADERS.includes(column.field)
                                    ? "table-cell"
                                    : "none",
                                  lg: "table-cell",
                                },
                                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                width: `${column.width}px`,
                                minWidth: `${column.width}px`,
                                maxWidth: `${column.width}px`,
                                color: isNegative
                                  ? theme.palette.error.main
                                  : "inherit",
                                fontWeight: isNegative ? 600 : "normal",
                              }}
                              title={cellValue}
                            >
                              {cellValue}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this position?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            aria-label="Cancel delete"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            aria-label="Confirm delete"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
